import { useState, useMemo, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import Logo from "/images/logo_dark.svg";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks";
import {
  postGrowthMarketingSetupThunk,
  fetchStubCampaignThunk,
} from "../../stores/thunks/onboardingThunk";
import { Step1_GettingStarted } from "./components/Step1_GettingStarted";
import { Step2_GrowthChallenges } from "./components/Step2_GrowthChallenges";
import { Step3_CreatorDiscovery } from "./components/Step3_CreatorDiscovery";
import { Step4_GenerateBrief } from "./components/Step4_GenerateBrief";
import { Step6_Done } from "./components/Step6_Done";
import {
  faCircleQuestion,
  faLightbulbOn,
} from "@fortawesome/pro-light-svg-icons";
import { createCreatorBriefThunk } from "../../stores/thunks/creatorBriefsThunk";
import { displayAlert } from "../../stores/slice/alertSlice";
import { HelpDialog } from "./components/HelpDialog";
import {
  RevAlert,
  RevButton,
  RevStepper,
  StepperDesktopItem,
} from "@revmatics/catalyst";
import { onboardingSteps } from "./components/data.ts";
import { StepHeader } from "./components/StepHeader.tsx";
import {
  setBriefsForGenerate,
  setBriefItemForGenerate,
  clearBriefStatuses,
  hydrateOnboardingState,
  setOnboardingResult,
} from "../../stores/slice/onboardingSlice.tsx";
import { CreatorBrief } from "../../stores/types/creatorBriefsTypes.tsx";
import { clearMatchedCreators } from "../../stores/slice/discoverCommunitiesSlice.tsx";
import { OnboardingPersistState } from "../../stores/types/onboardingTypes";
import { ProfileMenu } from "../../components/layout/components/header/ProfileMenu.tsx";
import { fetchBrandList } from "../../stores/thunks/brandThunk.tsx";

const ONBOARDING_STORAGE_KEY = "onboardingProgress";

export type OnboardingFormData = {
  websiteUrl: string;
  growthChallenge: string[];
  marketingBudget: number;
  conversionRate: number;
  creatorExperience: string;
  selectedPersona: number | null;
  selectedPairs: {
    personaId: number;
    creatorId: number;
  }[];
  contentStyle: string;
  validHelper: string;
};

export const OnboardingPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const dispatch = useAppDispatch();
  const [isCreatingBrief, setIsCreatingBrief] = useState(false);
  const [briefForCreatingCount, setBriefForCreatingCount] = useState<
    number | null
  >(null);

  const {
    personas,
    briefsForGenerate,
    briefStatuses,
    campaign,
    onboardingResult,
    profile,
    currentBrand,
    brandList,
  } = useAppSelector((state) => ({
    personas: state.personas.personas,
    briefsForGenerate: state.onboarding.briefsForGenerate,
    briefStatuses: state.onboarding.briefStatuses,
    campaign: state.onboarding.campaign,
    onboardingResult: state.onboarding.onboardingResult,
    profile: state.profile.profile,
    currentBrand: state.brand.currentBrand,
    brandList: state.brand.brandList,
  }));

  const methods = useForm<OnboardingFormData>({
    mode: "onChange",
    defaultValues: {
      websiteUrl: "",
      growthChallenge: [],
      selectedPersona: null,
      selectedPairs: [],
      contentStyle: "",
      validHelper: "",
    },
  });

  const {
    trigger,
    getValues,
    formState: { isValid },
    watch,
    reset,
  } = methods;

  const selectedPersona = watch("selectedPersona", null);
  const selectedPairs = watch("selectedPairs", []);
  const pollingControllers = useRef<Map<number, AbortController>>(new Map());
  const successSteps = useMemo(() => {
    return Array.from({ length: currentStep - 1 }, (_, i) => i + 1);
  }, [currentStep]);

  const isInitialMount = useRef(true);

  const generateBriefProccess = useMemo(() => {
    return Object.values(briefStatuses).includes("generating");
  }, [briefStatuses]);

  useEffect(() => {
    if (!brandList || brandList.length === 0) {
      dispatch(fetchBrandList());
    }
  }, [dispatch, brandList]);

  useEffect(() => {
    if (currentBrand && !campaign?.id && !onboardingResult?.campaign_id) {
      dispatch(fetchStubCampaignThunk(Number(currentBrand)))
        .unwrap()
        .then((result) => {
          dispatch(setOnboardingResult(result));
        })
        .catch((err) => {
          console.error("Failed to initialize onboarding campaign:", err);
        });
    }
  }, [dispatch, currentBrand, campaign?.id, onboardingResult?.campaign_id]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    try {
      const persistedStateString = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      const currentUserEmail = profile.email;

      if (persistedStateString) {
        const persistedState = JSON.parse(
          persistedStateString,
        ) as OnboardingPersistState;

        if (currentUserEmail && persistedState.email === currentUserEmail) {
          if (persistedState.currentStep) {
            setCurrentStep(persistedState.currentStep);
          }
          if (persistedState.selectedPairs || persistedState.selectedPersona) {
            reset({
              selectedPairs: persistedState.selectedPairs || [],
              selectedPersona: persistedState.selectedPersona || null,
            });
          }
          dispatch(hydrateOnboardingState(persistedState));
        } else {
          localStorage.removeItem(ONBOARDING_STORAGE_KEY);
          setCurrentStep(1);
        }
      }
    } catch (error) {
      console.error("Failed to load onboarding state:", error);
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    }
  }, [dispatch, reset, profile]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (currentStep === onboardingSteps.length + 1) return;
    if (!profile?.email) return;

    try {
      const campaignToPersist =
        campaign ||
        (onboardingResult
          ? {
              id: onboardingResult.campaign_id,
              name: onboardingResult.campaign_name,
            }
          : null);

      const stateToPersist: OnboardingPersistState = {
        currentStep,
        campaign: campaignToPersist,
        selectedPersona,
        selectedPairs,
        briefsForGenerate,
        briefStatuses,
        email: profile.email,
      };
      localStorage.setItem(
        ONBOARDING_STORAGE_KEY,
        JSON.stringify(stateToPersist),
      );
    } catch (error) {
      console.error("Failed to save onboarding state:", error);
    }
  }, [
    currentStep,
    campaign,
    onboardingResult,
    selectedPairs,
    selectedPersona,
    briefsForGenerate,
    briefStatuses,
    profile?.email,
  ]);

  useEffect(() => {
    if (
      isCreatingBrief &&
      briefsForGenerate.length === briefForCreatingCount &&
      currentStep === 3
    ) {
      setIsCreatingBrief(false);
      setCurrentStep(4);
    }
  }, [
    briefForCreatingCount,
    briefsForGenerate.length,
    currentStep,
    dispatch,
    isCreatingBrief,
  ]);

  const handleNext = async () => {
    if (currentStep === 1) {
      const isStepValid = await trigger("websiteUrl");
      if (!isStepValid) return;

      dispatch(setBriefsForGenerate([]));
      dispatch(clearBriefStatuses());
      dispatch(clearMatchedCreators());
      setCurrentStep(2);
    } else if (currentStep === 2) {
      const isStepValid = await trigger([
        "growthChallenge",
        "marketingBudget",
        "creatorExperience",
      ]);
      if (!isStepValid) return;

      const { growthChallenge, marketingBudget, creatorExperience } =
        getValues();

      const payload = {
        biggest_growth_challenge: growthChallenge[0] || "I don't know",
        monthly_marketing_budget: marketingBudget,
        worked_with_creators: creatorExperience,
      };

      dispatch(postGrowthMarketingSetupThunk(payload))
        .unwrap()
        .catch((error) => {
          console.error("Failed to post growth marketing data:", error);
          dispatch(
            displayAlert({
              type: "error",
              text: "Could not save your marketing setup. Please try again.",
            }),
          );
        });

      setCurrentStep(3);
    } else if (currentStep === 3) {
      if (isCreatingBrief) {
        return;
      }

      const campaignId = onboardingResult?.campaign_id || campaign?.id;

      if (!campaignId) {
        console.error("Cannot generate briefs without a campaign ID.");
        dispatch(
          displayAlert({
            type: "error",
            text: "Campaign data is not initialized. Retrying...",
          }),
        );
        if (currentBrand) {
          dispatch(fetchStubCampaignThunk(Number(currentBrand)))
            .unwrap()
            .then((result) => {
              dispatch(setOnboardingResult(result));
            });
        }
        return;
      }
      setIsCreatingBrief(true);

      const existingPairsSet = new Set(
        briefsForGenerate.map((b) => `${b.persona.id}-${b.creator.id}`),
      );
      const newPairs = selectedPairs.filter(
        (pair) => !existingPairsSet.has(`${pair.personaId}-${pair.creatorId}`),
      );
      const validPairsSet = new Set(
        selectedPairs.map((pair) => `${pair.personaId}-${pair.creatorId}`),
      );
      const updatedBriefsForGenerate = briefsForGenerate.filter((brief) =>
        validPairsSet.has(`${brief.persona.id}-${brief.creator.id}`),
      );

      dispatch(setBriefsForGenerate(updatedBriefsForGenerate));
      setBriefForCreatingCount(
        updatedBriefsForGenerate.length + newPairs.length,
      );

      newPairs.forEach((pair) => {
        const selectedPersonaObject = personas?.find(
          (p) => p.id === pair.personaId,
        );
        const interestGroupId = selectedPersonaObject?.parent_interests?.[0]?.id
          ? String(selectedPersonaObject.parent_interests[0].id)
          : null;

        if (!interestGroupId) {
          console.error(
            `Selected persona ${pair.personaId} is missing interest group data.`,
          );
          return;
        }
        const payload = {
          campaign: campaignId,
          persona: pair.personaId,
          interest_group: interestGroupId,
          creator: pair.creatorId,
        };
        dispatch(createCreatorBriefThunk(payload))
          .unwrap()
          .then((brief: CreatorBrief) =>
            dispatch(setBriefItemForGenerate(brief)),
          );
      });
    } else if (currentStep === 4) {
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      setCurrentStep(5);
    } else {
      setCurrentStep((prev) => (prev < 5 ? prev + 1 : prev));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <Step1_GettingStarted />;
      case 2:
        return <Step2_GrowthChallenges />;
      case 3:
        return <Step3_CreatorDiscovery />;
      case 4:
        return <Step4_GenerateBrief pollingControllers={pollingControllers} />;
      case 5:
        return <Step6_Done />;
      default:
        return null;
    }
  };
  const totalSteps = onboardingSteps.length;

  const currentStepTitle = useMemo(() => {
    return onboardingSteps.find(({ step }) => step === currentStep)?.title;
  }, [currentStep]);

  if (currentStep === 5) {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  }

  return (
    <div className="bg-monochrome-50 h-dvh flex flex-col">
      <header className="py-4 flex items-center justify-between px-6 bg-monochrome-900 border-b border-monochrome-200">
        <img src={Logo} alt="Company Logo" className="h-[35px] w-auto" />
        <ProfileMenu />
      </header>
      <div className="flex flex-col lg:flex-row h-[calc(100dvh-68px)] overflow-y-auto scroll-vertical-small bg-primary-25 ">
        <aside className="hidden lg:flex sticky top-0 flex-col justify-between h-full bg-primary-25 lg:bg-primary-50 w-[330px] p-4 border-r border-monochrome-200">
          <RevStepper
            stepperData={onboardingSteps}
            step={currentStep}
            successSteps={successSteps}
            isVertical={true}
          />
          <div className="space-y-2">
            {generateBriefProccess && (
              <RevAlert
                isDismissible={false}
                data={{
                  show: true,
                  id: "onboarding_generation_desktop",
                  text: "Generation of creator brief in progress...",
                  type: "info",
                }}
                className="!min-w-full"
              />
            )}
            <RevAlert
              isDismissible={false}
              customIcon={faLightbulbOn}
              data={{
                show: true,
                id: "onboarding_desktop",
                text: "You can always edit or add details in the platform later.",
                type: "info",
              }}
              className="!min-w-full"
            />
            <RevButton
              text="Help"
              variant="secondary"
              buttonColor="inherit"
              iconLeft={faCircleQuestion}
              handleClick={() => setIsHelpDialogOpen(true)}
              className="mt-3 w-full hidden lg:inline-flex"
            />
          </div>
        </aside>
        <main className="max-w-[1400px] m-[0_auto] flex-1 flex flex-col gap-6 justify-between lg:block p-6">
          <div>
            <div className="w-full lg:hidden space-y-2 mb-6">
              <StepperDesktopItem
                data={{
                  step: currentStep,
                  status: "default",
                  variant: "outline",
                  title:
                    onboardingSteps.find(({ step }) => step === currentStep)
                      ?.title || "Finally",
                }}
                step={currentStep}
                className="w-full"
              />
              {generateBriefProccess && (
                <RevAlert
                  isDismissible={false}
                  data={{
                    show: true,
                    id: "onboarding_generation_mobile",
                    text: "Generation of creator brief in progress...",
                    type: "info",
                  }}
                  className="!min-w-full"
                />
              )}
              <RevAlert
                isDismissible={false}
                customIcon={faLightbulbOn}
                data={{
                  show: true,
                  id: "onboarding_mobile",
                  text: "You can always edit or add details in the platform later.",
                  type: "info",
                }}
              />
            </div>
            <FormProvider {...methods}>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="w-full lg:pb-[90px]"
              >
                {currentStepTitle && (
                  <StepHeader
                    totalSteps={totalSteps}
                    currentStep={currentStep}
                    title={currentStepTitle}
                  />
                )}
                {renderStepContent()}
              </form>
            </FormProvider>
          </div>
          <RevButton
            text="Help"
            variant="secondary"
            buttonColor="inherit"
            iconLeft={faCircleQuestion}
            handleClick={() => setIsHelpDialogOpen(true)}
            className="w-fit lg:hidden mb-[68px]"
            buttonSize="extraSmall"
          />
        </main>
        <footer className="fixed z-10 bottom-0 right-0 bg-white shadow-elevation--1 py-2 px-4 lg:py-3 lg:px-6 w-full lg:w-[calc(100%-330px)]">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 lg:hidden">
            <RevStepper
              stepperData={onboardingSteps}
              step={currentStep}
              successSteps={successSteps}
            />
          </div>
          <div className="flex justify-between items-center">
            <div>
              {currentStep > 1 && (
                <RevButton
                  text="Back"
                  variant="secondary"
                  handleClick={handleBack}
                  className="w-[128px] lg:w-fit"
                />
              )}
            </div>
            {currentStep < 5 && (
              <RevButton
                text={currentStep === 4 ? "Finish" : "Next"}
                variant="primary"
                className="w-[128px] lg:w-fit"
                handleClick={handleNext}
                disabled={
                  (currentStep < 5 && !isValid) ||
                  (currentStep === 3 &&
                    (selectedPairs.length === 0 || !selectedPersona))
                }
                isLoading={isCreatingBrief}
              />
            )}
          </div>
        </footer>
      </div>
      <HelpDialog
        isOpen={isHelpDialogOpen}
        onClose={() => setIsHelpDialogOpen(false)}
      />
    </div>
  );
};
